import { memo, useEffect, useMemo, useRef } from 'react'
import WaveSurfer from 'wavesurfer.js'
import { IAudioItem } from 'types/audio'
import * as Styled from './styled'
import { AUDIO_ITEM_COLORS } from '../consts'
import { COLORS } from 'consts/colors'
import { useTimelineItemBodyRect } from 'hooks/timeline'
import { useWaveform } from 'hooks/useWaveform'
import { decodeUploadedPeaks } from 'services/waveform-manager/helpers'

interface IAudioWaveProps {
  audioItem: IAudioItem
  isActive: boolean
  isDraft: boolean
  fileSize?: number
  peaksData?: string
}

const getColorPerStateAndType = (state: string, type: string) => {
  const colorsMap: Record<string, string> = {
    active: COLORS.white,
    draft: COLORS.secondary,
    default: AUDIO_ITEM_COLORS[type].waveColor
  }

  return colorsMap[state]
}

const AudioWave = ({
  audioItem,
  isActive,
  isDraft,
  fileSize,
  peaksData
}: IAudioWaveProps) => {
  const { mediaPath, type, assetId, duration } = audioItem
  const containerRef = useRef<HTMLDivElement>(null)
  const waveSurferRef = useRef<WaveSurfer | null>(null)

  const trackState = isActive ? 'active' : isDraft ? 'draft' : 'default'

  const waveColor = getColorPerStateAndType(trackState, type)

  const playbackSpeed = audioItem.playbackSpeed ?? 1

  const serverSidePeaks = useMemo(
    () => (peaksData ? decodeUploadedPeaks(peaksData) : null),
    [peaksData]
  )

  const { peaks: workerPeaks } = useWaveform(
    serverSidePeaks,
    assetId,
    mediaPath,
    fileSize,
    duration
  )

  const peaks = serverSidePeaks ?? workerPeaks

  const { bodyWidth: audioWaveWidth, bodyLeft: audioWaveLeft } =
    useTimelineItemBodyRect({
      trimStart: audioItem.mediaStart / playbackSpeed,
      originalDuration: audioItem.mediaDuration,
      playbackSpeed
    })

  useEffect(() => {
    if (!containerRef.current || !peaks || !audioItem.duration) return

    const ws = WaveSurfer.create({
      container: containerRef.current,
      peaks: [peaks],
      duration: audioItem.duration,
      progressColor: waveColor,
      waveColor,
      barWidth: 1,
      barGap: 1,
      cursorWidth: 0,
      barRadius: 1,
      height: 28,
      interact: false
    })
    waveSurferRef.current = ws

    return () => {
      ws.destroy()
      waveSurferRef.current = null
    }
  }, [peaks, audioItem.duration, waveColor])

  if (!peaks) return null

  const audioWaveStyle = {
    width: `${audioWaveWidth}px`,
    left: `${audioWaveLeft}px`
  }

  return <Styled.AudioWaveWrapper ref={containerRef} style={audioWaveStyle} />
}

export default memo(AudioWave)
